import useLocaleLink from 'hooks/useLocaleLink';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { useCallback, useState, type KeyboardEvent } from 'react';

interface SearchBoxProps {
  placeholder?: string;
  className?: string;
  initialValue?: string;
  size?: 'default' | 'compact';
}

export default function SearchBox({ placeholder, className, initialValue = '', size = 'default' }: SearchBoxProps) {
  const t = useTranslations('support');
  const router = useRouter();
  const localeLink = useLocaleLink();
  const [query, setQuery] = useState(initialValue);

  const handleSearch = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const isOnSearchPage = router.pathname === '/support/search';
    if (isOnSearchPage) {
      router.replace({ pathname: '/support/search', query: { q: trimmed, tab: 'all', page: '1' } }, undefined, {
        shallow: true,
      });
    } else {
      window.location.href = `${localeLink}/support/search?q=${encodeURIComponent(trimmed)}`;
    }
  }, [query, router, localeLink]);

  const handleClear = useCallback(() => {
    setQuery('');
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const isCompact = size === 'compact';

  const containerClass = isCompact
    ? `flex h-8 items-center gap-2 rounded-lg border border-base-line-secondary px-2 ${className ?? 'w-[288px]'}`
    : `flex h-14 items-center gap-2 rounded-lg bg-base-fill-secondary px-3 ${className ?? 'w-full max-w-[600px]'}`;

  const inputClass = isCompact
    ? 'min-w-0 flex-1 bg-transparent text-sm text-base-label outline-none placeholder:text-base-label-40'
    : 'min-w-0 flex-1 bg-transparent text-md text-base-label outline-none placeholder:text-base-label-40';

  const defaultPlaceholder = isCompact ? t('searchCompactPlaceholder') : placeholder || t('searchPlaceholder');

  return (
    <div className={containerClass}>
      <i className="iconfont icon-search-fill shrink-0 text-base text-base-label-40" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={defaultPlaceholder}
        className={inputClass}
      />
      {query && (
        <button type="button" onClick={handleClear} className="shrink-0 text-base-label-40 hover:text-base-label">
          <i className="iconfont icon-close-line text-base" />
        </button>
      )}
    </div>
  );
}
